import React from "react";
import { useParams, useLocation } from "react-router-dom";
import ReportPageTemplate from "../components/ReportPageTemplate";
import {
  getPayrollCostReport,
  exportPayrollCostReport,
  getAttendanceSummaryReport,
  getManagerTeamAttendance,
  exportAttendanceReport,
  getLeaveTrendsReport,
  exportLeaveReport,
  getReimbursementSpendReport,
  exportReimbursementReport,
  getHeadcountReport,
  exportHeadcountReport,
} from "../../../../services/workspace/reports.service";

// ==================== REPORT CONFIGURATIONS ====================

const REPORT_CONFIGS = {
  "payroll-cost": {
    title: "Payroll Cost Report",
    breadcrumbs: [
      { label: "Reports", path: "/reports" },
      { label: "Payroll Cost Report", path: "" },
    ],
    fetchFunction: getPayrollCostReport,
    exportFunction: exportPayrollCostReport,
    tableHeaders: [
      { label: "Month", key: "month" },
      { label: "Department", key: "department" },
      { label: "Employee Count", key: "employee_count" },
      { label: "Gross Salary", key: "gross_salary" },
      { label: "Total Deductions", key: "deductions" },
      { label: "Net Salary", key: "net_salary" },
      { label: "Growth %", key: "growth" },
    ],
    filters: [
      { key: "date_start", label: "Start Date", type: "date" },
      { key: "date_end", label: "End Date", type: "date" },
      { key: "department_id", label: "Department", type: "select", options: [] },
    ],
    dataTransformer: (data) => {
      if (!Array.isArray(data)) return { data: [],  };

      const transformedData = data.map((item) => ({
        month: { data: item.month || "-", variant: "text" },
        department: { data: item.department || "All", variant: "text" },
        employee_count: { data: item.employee_count || 0, variant: "number" },
        gross_salary: { data: item.gross_salary || 0, variant: "currency" },
        deductions: { data: item.total_deductions || 0, variant: "currency" },
        net_salary: { data: item.net_salary || 0, variant: "currency" },
        growth: {
          data: `${item.growth_percentage || 0}%`,
          variant: "text",
          className: item.growth_percentage >= 0 ? "text-green-600" : "text-red-600",
        },
      }));

      return { data: transformedData,  };
    },
  },

  "attendance-summary": {
    title: "Attendance Summary Report",
    breadcrumbs: [
      { label: "Reports", path: "/reports" },
      { label: "Attendance Summary", path: "" },
    ],
    fetchFunction: async (params) => {
      try {
        // Try new Reports API first
        return await getAttendanceSummaryReport(params);
      } catch (error) {
        // Fallback: Use existing Manager Team Attendance API
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        const managerId = user.id;
        
        if (managerId) {
          const response = await getManagerTeamAttendance(managerId, {
            start_date: params.date_start,
            end_date: params.date_end,
            department_id: params.department_id,
          });
          
          // Transform manager API response to match report format
          return {
            data: response.data?.team_attendance || [],
            meta: {
              total: response.data?.team_attendance?.length || 0,
              current_page: 1,
              per_page: 100,
            },
          };
        }
        
        throw error;
      }
    },
    exportFunction: exportAttendanceReport,
    tableHeaders: [
      { label: "Employee", key: "employee" },
      { label: "Department", key: "department" },
      { label: "Total Days", key: "total_days" },
      { label: "Present", key: "present" },
      { label: "Absent", key: "absent" },
      { label: "Late", key: "late" },
      { label: "Overtime (hrs)", key: "overtime" },
      { label: "Attendance %", key: "percentage" },
    ],
    filters: [
      { key: "date_start", label: "Start Date", type: "date" },
      { key: "date_end", label: "End Date", type: "date" },
      { key: "department_id", label: "Department", type: "select", options: [] },
    ],
    dataTransformer: (data) => {
      if (!Array.isArray(data)) return { data: [],  };

      const transformedData = data.map((item) => ({
        employee: { data: item.employee_name || "-", variant: "text", className: "font-medium" },
        department: { data: item.department || "-", variant: "text" },
        total_days: { data: item.total_days || 0, variant: "number" },
        present: { data: item.present_days || 0, variant: "number" },
        absent: { data: item.absent_days || 0, variant: "number" },
        late: { data: item.late_count || 0, variant: "number" },
        overtime: { data: item.overtime_hours || 0, variant: "number" },
        percentage: {
          data: `${item.attendance_percentage || 0}%`,
          variant: "text",
          className: item.attendance_percentage >= 90 ? "text-green-600 font-semibold" : item.attendance_percentage >= 75 ? "text-yellow-600" : "text-red-600",
        },
      }));

      return { data: transformedData,  };
    },
  },

  "leave-trends": {
    title: "Leave Trends Report",
    breadcrumbs: [
      { label: "Reports", path: "/reports" },
      { label: "Leave Trends", path: "" },
    ],
    fetchFunction: getLeaveTrendsReport,
    exportFunction: exportLeaveReport,
    tableHeaders: [
      { label: "Leave Type", key: "leave_type" },
      { label: "Total Applications", key: "total_applications" },
      { label: "Approved", key: "approved" },
      { label: "Rejected", key: "rejected" },
      { label: "Pending", key: "pending" },
      { label: "Total Days", key: "total_days" },
      { label: "Encashment Liability", key: "encashment" },
    ],
    filters: [
      { key: "date_start", label: "Start Date", type: "date" },
      { key: "date_end", label: "End Date", type: "date" },
      { key: "department_id", label: "Department", type: "select", options: [] },
    ],
    dataTransformer: (data) => {
      if (!Array.isArray(data)) return { data: [],  };

      const transformedData = data.map((item) => ({
        leave_type: { data: item.leave_type || "-", variant: "text", className: "font-medium" },
        total_applications: { data: item.total_applications || 0, variant: "number" },
        approved: { data: item.approved_count || 0, variant: "number" },
        rejected: { data: item.rejected_count || 0, variant: "number" },
        pending: { data: item.pending_count || 0, variant: "number" },
        total_days: { data: item.total_days || 0, variant: "number" },
        encashment: { data: item.encashment_liability || 0, variant: "currency" },
      }));

      return { data: transformedData,  };
    },
  },

  "reimbursement-spend": {
    title: "Reimbursement Spend Report",
    breadcrumbs: [
      { label: "Reports", path: "/reports" },
      { label: "Reimbursement Spend", path: "" },
    ],
    fetchFunction: getReimbursementSpendReport,
    exportFunction: exportReimbursementReport,
    tableHeaders: [
      { label: "Category", key: "category" },
      { label: "Total Count", key: "count" },
      { label: "Total Amount", key: "amount" },
      { label: "Approved", key: "approved_amount" },
      { label: "Pending", key: "pending_amount" },
      { label: "Rejected", key: "rejected_amount" },
    ],
    filters: [
      { key: "date_start", label: "Start Date", type: "date" },
      { key: "date_end", label: "End Date", type: "date" },
      { key: "department_id", label: "Department", type: "select", options: [] },
    ],
    dataTransformer: (data) => {
      if (!Array.isArray(data)) return { data: [],  };
      const transformedData = data.map((item) => ({
        category: { data: item.category || "-", variant: "text", className: "font-medium" },
        count: { data: item.expense_count || 0, variant: "number" },
        amount: { data: item.total_amount || 0, variant: "currency" },
        approved_amount: { data: item.approved_amount || 0, variant: "currency" },
        pending_amount: { data: item.pending_amount || 0, variant: "currency" },
        rejected_amount: { data: item.rejected_amount || 0, variant: "currency" },
      }));
      return { data: transformedData,  };
    },
  },

  "headcount-attrition": {
    title: "Headcount & Attrition Report",
    breadcrumbs: [
      { label: "Reports", path: "/reports" },
      { label: "Headcount & Attrition", path: "" },
    ],
    fetchFunction: getHeadcountReport,
    exportFunction: exportHeadcountReport,
    tableHeaders: [
      { label: "Department", key: "department" },
      { label: "Opening", key: "opening" },
      { label: "New Joiners", key: "joiners" },
      { label: "Exits", key: "exits" },
      { label: "Closing", key: "closing" },
      { label: "Attrition %", key: "attrition" },
      { label: "Growth %", key: "growth" },
    ],
    filters: [
      { key: "date_start", label: "Start Date", type: "date" },
      { key: "date_end", label: "End Date", type: "date" },
    ],
    dataTransformer: (data) => {
      if (!Array.isArray(data)) return { data: [],  };

      const transformedData = data.map((item) => ({
        department: { data: item.department || "All", variant: "text", className: "font-medium" },
        opening: { data: item.opening_headcount || 0, variant: "number" },
        joiners: { data: item.new_joiners || 0, variant: "number" },
        exits: { data: item.exits || 0, variant: "number" },
        closing: { data: item.closing_headcount || 0, variant: "number" },
        attrition: {
          data: `${item.attrition_rate || 0}%`,
          variant: "text",
          className: item.attrition_rate > 15 ? "text-red-600" : item.attrition_rate > 10 ? "text-yellow-600" : "text-green-600",
        },
        growth: {
          data: `${item.growth_rate || 0}%`,
          variant: "text",
          className: item.growth_rate >= 0 ? "text-green-600" : "text-red-600",
        },
      }));

      return { data: transformedData,  };
    },
  },
};

// ==================== MAIN COMPONENT ====================

const AllReports = () => {
  const location = useLocation();
  const pathname = location.pathname;
  const reportType = pathname.split('/').pop(); // Get last segment
  const config = REPORT_CONFIGS[reportType];

  if (!config) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Report Not Found
          </h1>
          <p className="text-gray-600">
            The requested report type "{reportType}" does not exist.
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Available reports: {Object.keys(REPORT_CONFIGS).join(", ")}
          </p>
        </div>
      </div>
    );
  }

  return <ReportPageTemplate {...config} />;
};

export default AllReports;

